import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaTimes } from 'react-icons/fa';
import { FaBars } from 'react-icons/fa6';
import {
  FaGear,
  FaStar,
  FaArrowRightFromBracket as FaSignOutAlt,
  FaArrowRightToBracket as FaSignInAlt,
} from 'react-icons/fa6';
import { Link } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { AuthModal } from '../auth';

import Avatar from './Avatar';

interface HamburgerMenuProps {
  className?: string;
}

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user, isAuthenticated, logout } = useAuth();

  // エラーが発生した場合のリセット処理
  useEffect(() => {
    if (hasError) {
      const timer = setTimeout(() => {
        setHasError(false);
        setIsOpen(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasError]);

  // メニュー外クリック時の閉じる機能
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      try {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      } catch (error) {
        console.warn('Click outside handler error:', error);
        setIsOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      try {
        if (event.key === 'Escape') {
          setIsOpen(false);
        }
      } catch (error) {
        console.warn('Escape key handler error:', error);
        setIsOpen(false);
      }
    };

    // イベントリスナーを追加（エラーハンドリング付き）
    try {
      document.addEventListener('mousedown', handleClickOutside, { passive: true });
      document.addEventListener('keydown', handleEscapeKey, { passive: true });
    } catch (error) {
      console.warn('Failed to add event listeners:', error);
    }

    // クリーンアップ関数
    return () => {
      try {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      } catch (error) {
        console.warn('Failed to remove event listeners:', error);
      }
    };
  }, [isOpen]);

  const toggleMenu = useCallback(() => {
    try {
      setIsOpen(prev => {
        // メニューが開いている場合は確実に閉じる
        if (prev) {
          return false;
        }
        // メニューが閉じている場合は開く
        return true;
      });
    } catch (error) {
      console.warn('Toggle menu error:', error);
      // エラーが発生した場合は確実に閉じる
      setIsOpen(false);
    }
  }, []);

  const closeMenu = useCallback(() => {
    try {
      setIsOpen(false);
    } catch (error) {
      console.warn('Close menu error:', error);
      // エラーが発生した場合は強制的に閉じる
      setIsOpen(false);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      closeMenu(); // 先にメニューを閉じる
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
      // エラーが発生した場合はメニューを再開（安全に）
      try {
        setIsOpen(true);
      } catch (setStateError) {
        console.warn('Failed to reopen menu after logout error:', setStateError);
      }
    }
  }, [logout, closeMenu]);

  const handleAuthClick = useCallback(() => {
    closeMenu(); // メニューを閉じる
    setShowAuthModal(true); // ログインモーダルを開く
  }, [closeMenu]);

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      {/* エラー状態の表示 */}
      {hasError && (
        <div className="absolute -top-8 left-0 bg-red-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50">
          エラーが発生しました
        </div>
      )}

      {/* ハンバーガーメニューアイコン */}
      <button
        onClick={toggleMenu}
        className="flex items-center justify-center w-10 h-10 text-amber-100 hover:bg-amber-900 hover:bg-opacity-30 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-amber-300"
        aria-label="メニューを開く"
        disabled={hasError}
        data-testid="hamburger-menu-button"
      >
        {isOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
      </button>

      {/* ドロップダウンメニュー */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
          style={{
            animation: 'fadeIn 0.2s ease-out',
            transformOrigin: 'top right',
          }}
          onAnimationEnd={() => {
            // アニメーション完了後の処理（必要に応じて）
          }}
        >
          <style>
            {`
              @keyframes fadeIn {
                from {
                  opacity: 0;
                  transform: scale(0.95) translateY(-10px);
                }
                to {
                  opacity: 1;
                  transform: scale(1) translateY(0);
                }
              }
            `}
          </style>
          {isAuthenticated && user ? (
            // 認証済みユーザー向けメニュー
            <>
              {/* ユーザー情報表示 */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <Avatar
                    email={user.email}
                    username={user.username}
                    size={40}
                    alt="ユーザーアバター"
                    showTooltip={true}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user.username || 'ユーザー'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                </div>
              </div>

              {/* メニュー項目 */}
              <div className="py-1">
                <Link
                  to="/favorites"
                  onClick={closeMenu}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <FaStar className="mr-3 text-yellow-500" size={16} />
                  お気に入り一覧
                </Link>

                <Link
                  to="/user/settings"
                  onClick={closeMenu}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <FaGear className="mr-3 text-gray-500" size={16} />
                  アカウント設定
                </Link>

                <div className="border-t border-gray-100 my-1"></div>

                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <FaSignOutAlt className="mr-3 text-gray-500" size={16} />
                  ログアウト
                </button>
              </div>
            </>
          ) : (
            // 非認証ユーザー向けメニュー
            <div className="py-1">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm text-gray-600">
                  ログインして、コメントやお気に入り機能をご利用ください
                </p>
              </div>

              <button
                onClick={handleAuthClick}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <FaSignInAlt className="mr-3 text-blue-600" size={16} />
                ログイン or 新規登録
              </button>
            </div>
          )}
        </div>
      )}

      {/* ログインモーダル */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="login"
        onSuccess={() => {
          setShowAuthModal(false);
        }}
      />
    </div>
  );
};

export default HamburgerMenu;
